import {
  convertDateToYYYYMMDDFormat,
  getFirstDayOfMonth,
  getToday,
} from '@repo/ui';
import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import * as Yup from 'yup';

import { usePerformanceReportForProfessionals } from '../../../hooks/usePerformanceReportForProfessionals';
import {
  PerformanceReportForProfessionalsDetailedRow,
  PerformanceReportForProfessionalsQueryParams,
} from '../../../models/performance-report-for-professionals';
import { getOrderColumnByTableColumnId } from './helpers';
import { PerformanceReportForProfessionalsView } from './view';

export const PerformanceReportForProfessionals = () => {
  const {
    total,
    search,
    filters,
    summary,
    evolution,
    statusGet,
    statusGetDashboard,
    statusGetRanking,
    currentPage,
    rowsPerPage,
    orderColumn,
    saveFilters,
    detailedList,
    statusExport,
    orderDirection,
    topValidatedActivities,
    handleSearchRemote,
    debouncedRequestGetPerformanceReportForProfessionalsRanking,
    requestExportPerformanceReportForProfessionals,
    requestGetPerformanceReportForProfessionalsRanking,
    requestRefreshAllPerformanceReportForProfessionalsData,
  } = usePerformanceReportForProfessionals();

  const selectedActionRef = useRef<'search' | 'export'>('search');
  const hasRequestedInitialDataRef = useRef(false);
  const hasInitializedTableSearchEffectRef = useRef(false);

  const buildQueryParams =
    (): PerformanceReportForProfessionalsQueryParams => ({
      start: validation.values.starts,
      end: validation.values.ends,
      organizationHashId: validation.values.entity?.value,
      courseHashId: validation.values.course?.value,
      semesterId: validation.values.semester?.value,
      curricularUnitHashId: validation.values.unit?.value,
      groupHashIds: validation.values.groups.map((group) => group.value),
      workloadUntil:
        validation.values.workloadUntil === ''
          ? undefined
          : Number(validation.values.workloadUntil),
    });

  const defaultValues = {
    unit: filters?.unit || null,
    groups: filters?.groups || [],
    entity: filters?.entity || null,
    course: filters?.course || null,
    semester: filters?.semester || null,
    starts:
      filters?.starts || convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
    ends: filters?.ends || convertDateToYYYYMMDDFormat(getToday()),
    workloadUntil: filters?.workloadUntil ?? '',
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: defaultValues,
    validationSchema: Yup.object({
      entity: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      course: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      semester: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      unit: Yup.object()
        .shape({
          label: Yup.string(),
          value: Yup.string(),
        })
        .nullable(),
      groups: Yup.array().of(
        Yup.object()
          .shape({
            label: Yup.string(),
            value: Yup.string(),
          })
          .nullable(),
      ),
      starts: Yup.date().required('Campo obrigatório.').nullable(),
      ends: Yup.date().required('Campo obrigatório.').nullable(),
      workloadUntil: Yup.number()
        .transform((value, originalValue) => {
          return originalValue === '' ? null : value;
        })
        .min(0)
        .nullable(),
    }),
    onSubmit: async (values) => {
      const valuesToSubmit: PerformanceReportForProfessionalsQueryParams = {
        start: values.starts,
        end: values.ends,
        organizationHashId: values.entity?.value,
        courseHashId: values.course?.value,
        semesterId: values.semester?.value,
        curricularUnitHashId: values.unit?.value,
        groupHashIds: values.groups.map((group) => group.value),
        workloadUntil:
          values.workloadUntil === ''
            ? undefined
            : Number(values.workloadUntil),
      };

      if (selectedActionRef.current === 'search') {
        saveFilters(values);
        await requestRefreshAllPerformanceReportForProfessionalsData(
          valuesToSubmit,
        );
      }

      if (selectedActionRef.current === 'export') {
        await requestExportPerformanceReportForProfessionals(valuesToSubmit);
      }
    },
  });

  useEffect(() => {
    if (hasRequestedInitialDataRef.current) {
      return;
    }

    hasRequestedInitialDataRef.current = true;

    saveFilters(validation.values);
    requestRefreshAllPerformanceReportForProfessionalsData(buildQueryParams());
  }, [requestRefreshAllPerformanceReportForProfessionalsData, saveFilters]);

  useEffect(() => {
    if (!hasInitializedTableSearchEffectRef.current) {
      hasInitializedTableSearchEffectRef.current = true;
      return;
    }

    debouncedRequestGetPerformanceReportForProfessionalsRanking({
      filters: buildQueryParams(),
      page: 0,
      length: rowsPerPage,
      orderColumn,
      orderDirection,
      search,
    });
  }, [
    debouncedRequestGetPerformanceReportForProfessionalsRanking,
    orderColumn,
    orderDirection,
    rowsPerPage,
    search,
  ]);

  return (
    <PerformanceReportForProfessionalsView
      total={total}
      summary={summary}
      evolution={evolution}
      statusGet={statusGet}
      statusGetDashboard={statusGetDashboard}
      statusGetRanking={statusGetRanking}
      validation={validation}
      currentPage={currentPage}
      rowsPerPage={rowsPerPage}
      statusExport={statusExport}
      detailedList={detailedList}
      topValidatedActivities={topValidatedActivities}
      onSearchClick={() => {
        selectedActionRef.current = 'search';
      }}
      onExportClick={() => {
        selectedActionRef.current = 'export';
      }}
      onClearFilters={() => {
        validation.setValues({
          entity: null,
          course: null,
          semester: null,
          unit: null,
          groups: [],
          starts: convertDateToYYYYMMDDFormat(getFirstDayOfMonth()),
          ends: convertDateToYYYYMMDDFormat(getToday()),
          workloadUntil: '',
        });
      }}
      onChangePage={async (page) => {
        await requestGetPerformanceReportForProfessionalsRanking({
          filters: buildQueryParams(),
          page: page - 1,
          length: rowsPerPage,
          orderColumn,
          orderDirection,
          search,
        });
      }}
      onChangeRowsPerPage={async (nextRowsPerPage) => {
        await requestGetPerformanceReportForProfessionalsRanking({
          filters: buildQueryParams(),
          page: 0,
          length: nextRowsPerPage,
          orderColumn,
          orderDirection,
          search,
        });
      }}
      onSort={async (
        selectedColumn: TableColumn<PerformanceReportForProfessionalsDetailedRow>,
        sortDirection: SortOrder,
      ) => {
        await requestGetPerformanceReportForProfessionalsRanking({
          filters: buildQueryParams(),
          page: 0,
          length: rowsPerPage,
          orderColumn: getOrderColumnByTableColumnId(Number(selectedColumn.id)),
          orderDirection: sortDirection.toUpperCase() as 'ASC' | 'DESC',
          search,
        });
      }}
      onTableSearch={async (value) => {
        handleSearchRemote(value);
      }}
    />
  );
};
